import type { CaptureDiagnosis } from '@tunasong/music-lib'

/**
 * The one-line diagnostic readout shown under a "we heard nothing" verdict.
 *
 * UI status, deliberately not lexicon copy: it is a set of measurements, not pedagogy, and it
 * exists because the failure state was previously the only screen in the tool that printed no
 * numbers at all. Whether the 0.90 clarity gate is wrong for a real voice through a laptop
 * microphone is unanswerable without this line, and a user can read it back verbatim.
 *
 * @example 'Peak −38 dB · floor −52 dB · best clarity 0.83 · voiced 4 %'
 */

/** Whole decibels with a real minus sign — `-38` reads as a hyphenated range at small sizes. */
const db = (value: number) => {
  const rounded = Math.round(value)
  return rounded < 0 ? `−${Math.abs(rounded)}` : `${rounded}`
}

export const formatCaptureReadout = (diagnosis: CaptureDiagnosis): string =>
  [
    `Peak ${db(diagnosis.peakDb)} dB`,
    `floor ${db(diagnosis.noiseFloorDb)} dB`,
    /** Two decimals, because the gate sits at 0.90 and 0.89-vs-0.90 is the entire question. */
    `best clarity ${diagnosis.bestClarity.toFixed(2)}`,
    `voiced ${Math.round(diagnosis.voicedFraction * 100)} %`,
  ].join(' · ')
